// DRV-5a (#2883) — Settings → Google Drive connection panel.
//
// Admin Settings path only: rendered by AuthorizeGDrivePage in mode='settings',
// which SettingsDriveAuthPage mounts behind its admin gate. The wizard path
// (/setup/authorize-gdrive, reachable by an assigned TL) never renders it.
//
// Reads GET /tenant-integrations/me on mount; every Drive detail shown comes
// from that response. "Last connected" is account.capturedAt, which the
// callback rewrites on every (re)connect. "Connection health" is the token
// refresh health (googleDriveAuth.ts recordHealth), NOT the last filing —
// filing status arrives with DRV-4 / DRV-5b.
//
// Connect/Reconnect hand back to the page (onConnect), which owns the OAuth
// popup. Disconnect is here: a confirm step, then DELETE
// /tenant-integrations/gdrive (admin rank, enforced server-side), then a
// fresh read.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { safeDriveUrl } from '../../lib/driveUrl';
import {
  driveDisplayState,
  fetchDriveIntegration,
  formatDriveDate,
  type DriveIntegration,
} from '../../lib/driveIntegration';

interface Props {
  /** Start the OAuth popup flow (Connect or Reconnect). */
  onConnect: () => void;
}

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; integration: DriveIntegration };

export default function DriveConnectionPanel({ onConnect }: Props) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectFailed, setDisconnectFailed] = useState(false);

  const refresh = useCallback(() => {
    setLoad({ kind: 'loading' });
    fetchDriveIntegration()
      .then((integration) => setLoad({ kind: 'ready', integration }))
      .catch(() => setLoad({ kind: 'error' }));
  }, []);

  useEffect(() => {
    let active = true;
    fetchDriveIntegration()
      .then((integration) => { if (active) setLoad({ kind: 'ready', integration }); })
      .catch(() => { if (active) setLoad({ kind: 'error' }); });
    return () => { active = false; };
  }, []);

  function disconnect() {
    setDisconnecting(true);
    setDisconnectFailed(false);
    apiFetch('/tenant-integrations/gdrive', { method: 'DELETE' })
      .then(() => {
        setConfirmingDisconnect(false);
        refresh();
      })
      .catch(() => setDisconnectFailed(true))
      .finally(() => setDisconnecting(false));
  }

  if (load.kind === 'loading') {
    return (
      <section data-testid="drive-panel" style={panelStyle}>
        <h2 style={headingStyle}>Google Drive</h2>
        <p data-testid="drive-panel-loading" style={mutedStyle}>Checking the Drive connection…</p>
      </section>
    );
  }

  if (load.kind === 'error') {
    return (
      <section data-testid="drive-panel" style={panelStyle}>
        <h2 style={headingStyle}>Google Drive</h2>
        <p data-testid="drive-panel-load-error" style={mutedStyle}>
          Couldn&rsquo;t load the Drive connection status.
        </p>
        <button data-testid="drive-panel-retry-load" style={secondaryBtnStyle} onClick={refresh}>
          Try again
        </button>
      </section>
    );
  }

  const { integration } = load;
  const state = driveDisplayState(integration);
  const account = integration.metadata?.account ?? null;
  const health = integration.metadata?.health ?? null;
  const connected = state === 'connected' || state === 'connected_unknown';

  return (
    <section data-testid="drive-panel" data-state={state} style={panelStyle}>
      <h2 style={headingStyle}>Google Drive</h2>

      {state === 'connected' && (
        <>
          <p data-testid="drive-panel-connected" style={bodyStyle}>
            Connected as <strong data-testid="drive-panel-email">{account?.emailAddress}</strong>
          </p>
          {formatDriveDate(account?.capturedAt) && (
            <p data-testid="drive-panel-last-connected" style={mutedStyle}>
              Last connected {formatDriveDate(account?.capturedAt)}
            </p>
          )}
        </>
      )}

      {state === 'connected_unknown' && (
        <p data-testid="drive-panel-connected-unknown" style={bodyStyle}>
          Connected &mdash; reconnect to record the account.
        </p>
      )}

      {connected && <HealthLine health={health} />}

      {connected && <RootFolderLink />}

      {state === 'revoked' && (
        <p data-testid="drive-panel-revoked" role="alert" style={bannerStyle}>
          Google Drive access was revoked or has expired. BluePrint can&rsquo;t read or write
          documents until you reconnect.
        </p>
      )}

      {state === 'admin_policy' && (
        <p data-testid="drive-panel-admin-policy" role="alert" style={bannerStyle}>
          Your Google Workspace admin has blocked BluePrint. If they&rsquo;ve since allowed it,
          reconnect.
        </p>
      )}

      {state === 'not_connected' && (
        <p data-testid="drive-panel-not-connected" style={bodyStyle}>Not connected.</p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        {state === 'not_connected' && (
          <button data-testid="drive-panel-connect" style={primaryBtnStyle} onClick={onConnect}>
            Connect Google Drive
          </button>
        )}
        {(connected || state === 'revoked' || state === 'admin_policy') && !confirmingDisconnect && (
          <button data-testid="drive-panel-reconnect" style={connected ? secondaryBtnStyle : primaryBtnStyle} onClick={onConnect}>
            Reconnect
          </button>
        )}
        {connected && !confirmingDisconnect && (
          <button data-testid="drive-panel-disconnect" style={dangerBtnStyle} onClick={() => setConfirmingDisconnect(true)}>
            Disconnect
          </button>
        )}
      </div>

      {connected && confirmingDisconnect && (
        <div data-testid="drive-panel-disconnect-confirm-step" style={confirmBoxStyle}>
          <p style={{ ...bodyStyle, margin: '0 0 12px' }}>
            Disconnect Google Drive? BluePrint will stop reading and writing documents in Drive
            until someone reconnects. Files already in Drive stay there.
          </p>
          {disconnectFailed && (
            <p data-testid="drive-panel-disconnect-error" role="alert" style={errorTextStyle}>
              Couldn&rsquo;t disconnect. Please try again.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button data-testid="drive-panel-disconnect-confirm" style={dangerBtnStyle} onClick={disconnect} disabled={disconnecting}>
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
            <button
              data-testid="drive-panel-disconnect-cancel"
              style={secondaryBtnStyle}
              onClick={() => { setConfirmingDisconnect(false); setDisconnectFailed(false); }}
              disabled={disconnecting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// DRV-5b(i) (#2883) — GET /tenant/storage/folders/root (TENANT_ADMIN rank,
// server-side). Shown only when the server sent a webUrl (active root) and
// lib/driveUrl.ts accepts it; otherwise nothing renders.
function RootFolderLink() {
  const [href, setHref] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    apiFetch<{ status: string; webUrl?: string }>('/tenant/storage/folders/root')
      .then((data) => { if (active) setHref(safeDriveUrl(data.webUrl)); })
      .catch(() => { if (active) setHref(null); });
    return () => { active = false; };
  }, []);
  if (!href) return null;
  return (
    <p style={{ ...mutedStyle, marginTop: 8 }}>
      <a data-testid="drive-panel-root-folder" href={href} target="_blank" rel="noopener noreferrer">
        Open BluePrint folder in Drive ↗
      </a>
    </p>
  );
}

type DriveHealth = NonNullable<DriveIntegration['metadata']>['health'];

// DRV-5b(ii) D-7 (#2883) — health.lastError is a code, never shown raw. The
// writers: googleDriveAuth.ts failWith (the 12 DriveAuthErrorCode values) and
// markRevoked ('invalid_grant', written directly, not via failWith; R1-7),
// enqueueFiling.ts and storageFolderService.ts. Any other code gets the fallback.
const HEALTH_COPY = new Map<string, string>(Object.entries({
  not_connected:          "Google Drive isn't connected",
  token_replaced:         'the Drive connection was replaced by a newer one',
  not_configured:         "Google Drive isn't set up on this server",
  revoked:                'Google Drive access was revoked or has expired',
  invalid_grant:          'Google Drive access was revoked or has expired',
  decrypt_failed:         "BluePrint couldn't read the saved Drive connection",
  network:                "BluePrint couldn't reach Google",
  timeout:                'Google took too long to respond',
  google_5xx:             'Google Drive was unavailable',
  rate_limited:           'Google Drive asked BluePrint to slow down',
  invalid_client:         "BluePrint's Google sign-in isn't set up correctly",
  token_http_error:       'Google refused to renew the connection',
  malformed_response:     "Google sent a response BluePrint couldn't read",
  filing_enqueue_failed:  "a document couldn't be queued for filing",
  folders_held_elsewhere: 'these Drive folders belong to another workspace',
  account_shared:         'this Google account is connected to another workspace',
}));
export const HEALTH_FALLBACK = 'Something went wrong with Drive. Try Reconnect.';

export function healthErrorCopy(code: string | null | undefined): string {
  const copy = code ? HEALTH_COPY.get(code) : undefined;
  return copy ? `${copy}.` : HEALTH_FALLBACK;
}

function HealthLine({ health }: { health: DriveHealth }) {
  const okAt = health?.lastOkAt ?? null;
  const errAt = health?.lastErrorAt ?? null;
  const failingNow = !!health?.lastError && (!okAt || (!!errAt && Date.parse(errAt) > Date.parse(okAt)));
  if (failingNow) {
    return (
      <p data-testid="drive-panel-health" data-health="error" role="alert" style={bannerStyle}>
        Connection health: the last check failed
        {formatDriveDate(errAt) ? ` (${formatDriveDate(errAt)})` : ''}: {healthErrorCopy(health?.lastError)}
      </p>
    );
  }
  if (okAt && formatDriveDate(okAt)) {
    return (
      <p data-testid="drive-panel-health" data-health="ok" style={mutedStyle}>
        Connection health: OK, last checked {formatDriveDate(okAt)}
      </p>
    );
  }
  return (
    <p data-testid="drive-panel-health" data-health="unknown" style={mutedStyle}>
      Connection health: not checked yet
    </p>
  );
}

// ── Styles (inline-style + CSS-var idiom, as AuthorizeGDrivePage) ─────────────

const panelStyle: React.CSSProperties = {
  width: '100%', maxWidth: 560, background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl, 16px)', boxShadow: 'var(--shadow-lg)', padding: 32,
};
const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 12px',
};
const bodyStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', lineHeight: 1.5, margin: '0 0 6px',
};
const mutedStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 6px',
};
const bannerStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.5, margin: '8px 0',
  padding: '10px 12px', borderRadius: 'var(--radius-sm, 8px)',
  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)',
};
const errorTextStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error, #ef4444)', margin: '0 0 12px',
};
const confirmBoxStyle: React.CSSProperties = {
  marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm, 8px)',
};
const primaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, padding: '8px 18px',
  background: 'var(--primary, #7c3aed)', color: '#fff', border: 'none',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500, padding: '8px 18px',
  background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
const dangerBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, padding: '8px 18px',
  background: 'transparent', color: 'var(--error, #ef4444)', border: '1px solid var(--error, #ef4444)',
  borderRadius: 'var(--radius-sm, 8px)', cursor: 'pointer',
};
